import appConfig from '../config';
import { useState } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import StakeTable, { Column } from "./GameEngine/StakeTable";

const SubAdminUser = () => {
  const queryClient = useQueryClient();
  const baseURL = appConfig.baseURL;

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "admin",
    amount: "0"
  });
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error">("error");
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: myProfile } = useQuery({
    queryKey: ['myProfile'],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const response = await axios.get(`${baseURL}/admin/getMineProfile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data?.data;
    }
  });

  const availableBalance = myProfile?.available_credit !== undefined ? parseFloat(myProfile.available_credit) : null;

  const { data: adminsData, isLoading: isLoadingAdmins } = useQuery({
    queryKey: ['collaborators'],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      if (!token) return { data: [] };
      const response = await axios.get(`${baseURL}/collaborator/list?role=manager`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    }
  });
  const amountNum = parseFloat(formData.amount || "0");
  const exceedsBalance = availableBalance !== null && amountNum > availableBalance;

  const createMutation = useMutation({
    mutationFn: async (payload: any) => {
      const token = localStorage.getItem("token");
      return axios.post(`${baseURL}/collaborator/create`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType("success");
      setFormData({ username: "", email: "", password: "", role: "admin", amount: "" });
      setShowModal(false);
      queryClient.invalidateQueries({ queryKey: ['collaborators'] });
      queryClient.invalidateQueries({ queryKey: ['myProfile'] });
    },
    onError: (error: any) => {
      console.error("Error creating user:", error);
      setMessage(error.response?.data?.message || "Failed to create user");
      setMessageType("error");
    }
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage("");

    if (exceedsBalance) {
      setMessage(`Insufficient balance. You only have USD ${availableBalance.toFixed(2)} available.`);
      setMessageType("error");
      return;
    }

    createMutation.mutate(formData);
  };

  const filteredAdmins = searchTerm.trim()
    ? (adminsData?.data || []).filter((item: any) =>
      (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    : (adminsData?.data || []);

  const columns: Column[] = [
    { key: 'sn', label: 'S.No.', render: (_, __, i) => i + 1 },
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'referalcode', label: 'Referral Code' },
    { key: 'referral_link', label: 'Refer Link' },
    { key: 'created', label: 'Date', render: (val) => new Date(val).toLocaleDateString() }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="ml-0 lg:ml-[256px] min-h-screen text-white p-6 bg-[#151929]">
      <h2 className="text-xl sm:text-3xl font-bold mb-6 text-white">Collaborators Management</h2>

      <div className="mb-6">
        <StakeTable
          title=""
          columns={columns}
          data={filteredAdmins}
          searchPlaceholder="Search by name or email..."
          onSearch={(val) => setSearchTerm(val)}
          emptyMessage={isLoadingAdmins ? "Loading collaborators..." : "No collaborators found."}
          hasSidebarMargin={false}
          actions={
            <button
              onClick={() => setShowModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded flex items-center transition-colors shadow-sm whitespace-nowrap text-sm"
            >
              + Create Collaborator
            </button>
          }
        />
      </div>

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm z-50 p-4">
          <div className="bg-slate-900 border border-slate-700/60 shadow-2xl  p-6 rounded-lg shadow-md w-full max-w-md">
            <h2 className="text-xl font-semibold mb-1">Create Collaborators</h2>

            {/* Balance reminder inside modal */}
            {availableBalance !== null && (
              <div className={`mb-4 text-sm rounded px-3 py-2 ${availableBalance <= 0 ? "bg-red-900 text-red-300" : "bg-green-900 text-green-300"}`}>
                Available to distribute:{" "}
                <span className="font-bold">USD {availableBalance.toFixed(2)}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium">Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="w-full p-2 mt-1 rounded bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  text-white border"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full p-2 mt-1 rounded bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  text-white border"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium">Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full p-2 mt-1 rounded bg-slate-950/50 border border-slate-700/50 focus:border-indigo-500  text-white border"
                />
              </div>
              {/* <div className="mb-4">
                <label className="block text-sm font-medium">
                  Amount to give user
                  {availableBalance !== null && (
                    <span className="ml-2 text-xs text-gray-400">(max: USD {availableBalance})</span>
                  )}
                </label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  min="1"
                  max={availableBalance ?? undefined}
                  className={`w-full p-2 mt-1 rounded bg-gray-700 text-white border ${exceedsBalance ? "border-red-500" : ""
                    }`}
                />
                {exceedsBalance && (
                  <p className="text-red-400 text-xs mt-1">
                    ⚠ Exceeds your available balance of USD {availableBalance}
                  </p>
                )}
              </div> */}

              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="p-2 bg-red-600 hover:bg-red-700 rounded text-white font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || exceedsBalance}
                  className="p-2 bg-blue-600 hover:bg-blue-700 rounded text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createMutation.isPending ? "Creating..." : "Create User"}
                </button>
              </div>

              {message && (
                <p className={`mt-4 text-center text-sm ${messageType === "success" ? "text-green-400" : "text-red-400"}`}>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubAdminUser;
